import type { Abi, Address, Hash } from 'viem'
import { maxUint256 } from 'viem'
import { useCallback, useMemo } from 'react'
import { useAccount, useChainId, usePublicClient } from 'wagmi'

import { describeWalletSigningError } from '../domain/wallet/resolveWalletClient'
import { submitContractWrite } from '../domain/wallet/submitContractWrite'
import { beginWalletSigning, endWalletSigning } from '../domain/wallet/walletSigningFlow'
import { getNativeChainIdForCurrency } from '../config/native'
import { Trade, TokenAmount, CurrencyAmount } from '../sdk'
import { ROUTER_ADDRESS } from '../constants'
import ERC20_ABI_JSON from '../constants/abis/erc20.json'
import { useTokenAllowance } from '../data/Allowances'
import { buildApproveInvalidateCalls } from '../state/multicall/buildInvalidateCalls'
import { Field } from '../state/swap/actions'
import { useTransactionAdder, useHasPendingApproval } from '../state/transactions/hooks'
import { computeSlippageAdjustedAmounts } from '../utils/prices'
import { calculateGasMarginViem } from '../utils/swapParametersViem'
import { txSubmitDebug, txSubmitWarn } from '../utils/debugTxSubmit'
import { useAppWeb3 } from './index'

const ERC20_ABI = ERC20_ABI_JSON as Abi

export enum ApprovalState {
  UNKNOWN,
  NOT_APPROVED,
  PENDING,
  APPROVED,
}

// returns a variable indicating the state of the approval and a function which approves if necessary or early returns
export function useApproveCallback(
  amountToApprove?: CurrencyAmount,
  spender?: string,
): [ApprovalState, () => Promise<void>] {
  const { account } = useAppWeb3()
  const { address: wagmiAccount, connector } = useAccount()
  const chainId = useChainId()
  const publicClient = usePublicClient()

  const token = amountToApprove instanceof TokenAmount ? amountToApprove.token : undefined
  const currentAllowance = useTokenAllowance(token, account ?? undefined, spender)
  const pendingApproval = useHasPendingApproval(token?.address, spender)

  // check the current approval status
  const approvalState: ApprovalState = useMemo(() => {
    if (!amountToApprove || !spender) return ApprovalState.UNKNOWN
    if (getNativeChainIdForCurrency(amountToApprove.currency) !== undefined) return ApprovalState.APPROVED
    if (!currentAllowance) return ApprovalState.UNKNOWN

    return currentAllowance.lessThan(amountToApprove)
      ? pendingApproval
        ? ApprovalState.PENDING
        : ApprovalState.NOT_APPROVED
      : ApprovalState.APPROVED
  }, [amountToApprove, currentAllowance, pendingApproval, spender])

  const addTransaction = useTransactionAdder()

  const approve = useCallback(async (): Promise<void> => {
    if (approvalState !== ApprovalState.NOT_APPROVED) {
      txSubmitWarn('approve skipped: unnecessary call', { approvalState })
      return
    }
    if (!token) {
      txSubmitWarn('approve skipped: no token', {})
      return
    }

    if (!amountToApprove) {
      txSubmitWarn('approve skipped: missing amount', {})
      return
    }

    if (!spender) {
      txSubmitWarn('approve skipped: no spender', {})
      return
    }

    if (!wagmiAccount || !publicClient || !connector) {
      txSubmitWarn('approve skipped: wallet or RPC not available', {
        hasWagmiAccount: Boolean(wagmiAccount),
        hasPublicClient: Boolean(publicClient),
        hasConnector: Boolean(connector),
      })
      throw new Error(
        describeWalletSigningError({
          account: wagmiAccount,
          connector,
          chainId: publicClient?.chain?.id,
          publicClient,
        }),
      )
    }

    txSubmitDebug('approve pipeline begin', {
      token: token.address,
      spender,
      account: wagmiAccount,
    })

    const tokenAddr = token.address as Hash
    const spenderAddr = spender as Hash
    const exactAmount = BigInt(amountToApprove.raw.toString())

    let approvalAmount = maxUint256
    try {
      await publicClient.estimateContractGas({
        address: tokenAddr,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [spenderAddr, maxUint256],
        account: wagmiAccount,
      })
    } catch {
      approvalAmount = exactAmount
    }

    const { request } = await publicClient.simulateContract({
      address: tokenAddr,
      abi: ERC20_ABI,
      functionName: 'approve',
      args: [spenderAddr, approvalAmount],
      account: wagmiAccount,
    } as Parameters<NonNullable<typeof publicClient.simulateContract>>[0])

    txSubmitDebug('approve simulate ok', {
      approvalAmount: approvalAmount.toString(),
      gasFromRequest: request.gas?.toString(),
    })

    const gas = request.gas ? calculateGasMarginViem(request.gas) : undefined

    txSubmitDebug('approve writeContract call', { token: tokenAddr, spender: spenderAddr })

    beginWalletSigning()
    let hash: Hash
    try {
      hash = await submitContractWrite({
        address: request.address,
        abi: request.abi,
        functionName: request.functionName,
        args: request.args as readonly unknown[],
        account: wagmiAccount as Address,
        chainId,
        connector,
        gas,
      })
      endWalletSigning()
    } catch (error) {
      endWalletSigning()
      throw error
    }

    txSubmitDebug('approve writeContract returned hash', { hash })

    addTransaction(
      { hash },
      {
        summary: 'Approve ' + amountToApprove.currency.symbol,
        approval: { tokenAddress: token.address, spender: spender },
        multicallInvalidateCalls: account ? buildApproveInvalidateCalls(account, token.address, spender) : undefined,
      },
    )
  }, [
    approvalState,
    token,
    amountToApprove,
    spender,
    addTransaction,
    account,
    wagmiAccount,
    chainId,
    publicClient,
    connector,
  ])

  return [approvalState, approve]
}

// wraps useApproveCallback in the context of a swap
export function useApproveCallbackFromTrade(trade?: Trade, allowedSlippage = 0) {
  const amountToApprove = useMemo(
    () => (trade ? computeSlippageAdjustedAmounts(trade, allowedSlippage)[Field.INPUT] : undefined),
    [trade, allowedSlippage],
  )
  return useApproveCallback(amountToApprove, ROUTER_ADDRESS)
}
